import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { BASE_URL, API_TOKEN, SLINK } from "../../config";
import { FaShoppingCart } from "react-icons/fa"; // Import the cart icon
import { ThemeContext } from "../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, Grid, List } from "lucide-react"; // Import icons
import { useCart } from "../../contexts/CartContext";

const SwissbarDisplay = () => {
  const [swissBars, setSwissBars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isDarkMode } = useContext(ThemeContext);
  const { currency } = useCart();
  const navigate = useNavigate();

  // New state for search, sorting, and layout
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [sortOption, setSortOption] = useState("relevance"); // Sort option state
  const [isGridLayout, setIsGridLayout] = useState(true); // Layout state

  useEffect(() => {
    const fetchSwissBars = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/user/catalogues`, {
          params: { slink: SLINK },
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        console.log('response 1', response);

        setSwissBars(response.data.data[0].campaign_entitycards);
        console.log(response.data.data[0].campaign_entitycards);
      } catch (error) {
        console.error("Error fetching Swiss bars:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSwissBars();
  }, []);

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  };

  if (isLoading) {
    return (
      <div
        className={`flex justify-center items-center h-screen ${
          isDarkMode ? "bg-black text-white" : "bg-white text-black"
        }`}
      >
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  // Filter products based on search term
  const filteredBars = swissBars.filter(
    (item) =>
      item.product.name &&
      item.product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort products based on selected option
  const sortedBars = [...filteredBars].sort((a, b) => {
    if (sortOption === "lowToHigh") {
      return a.product.price - b.product.price; // Sort by price ascending
    } else if (sortOption === "highToLow") {
      return b.product.price - a.product.price; // Sort by price descending
    } else {
      return 0; // Default case for relevance or other options
    }
  });

  return (
    <div
      className={`container mx-auto px-4 sm:px-20 py-6 ${
        isDarkMode ? "bg-black text-white" : "bg-white text-black"
      }`}
    >
      <h1 className="text-2xl font-bold mb-6 text-center">Swiss Bars</h1>

      {/* Search and Sort Container for Desktop View */}
      <div className="hidden sm:flex mb-4 flex-col sm:flex-row gap-4 items-center">
        {/* Search Input */}
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} // Handle search input
            className="w-full py-2 pl-10 pr-4 text-black placeholder-gray-500 bg-white border-2 border-yellow-500 rounded-lg focus:outline-none focus:border-yellow-600"
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="appearance-none w-32 py-2 pl-4 pr-10 text-black bg-yellow-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-600"
          >
            <option value="relevance">Relevance</option>
            <option value="lowToHigh">Low to High</option>
            <option value="highToLow">High to Low</option>
          </select>
          <ChevronDown
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black pointer-events-none"
            size={20}
          />
        </div>

        {/* Toggle Layout Button */}
        <button
          onClick={() => setIsGridLayout(!isGridLayout)}
          className="p-2 rounded-lg text-white bg-yellow-500 focus:outline-none"
        >
          {isGridLayout ? <Grid size={20} /> : <List size={20} />}
        </button>
      </div>

      {/* Search and Sort Container for Mobile View */}
      <div className="flex sm:hidden mb-4 flex-col gap-4 items-center">
        {/* Search Input */}
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} // Handle search input
            className="w-full py-2 pl-10 pr-4 text-black placeholder-gray-500 bg-white border-2 border-yellow-500 rounded-lg focus:outline-none focus:border-yellow-600"
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
        </div>

        {/* Sort Dropdown and Toggle Layout Button in the same row for mobile view */}
        <div className="flex sm:flex-row gap-4 items-center">
          {/* Sort Dropdown */}
          <div className="relative flex-grow">
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="appearance-none w-full py-2 pl-4 pr-10 text-black bg-yellow-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-600"
            >
              <option value="relevance">Relevance</option>
              <option value="lowToHigh">Low to High</option>
              <option value="highToLow">High to Low</option>
            </select>
            <ChevronDown
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black pointer-events-none"
              size={20}
            />
          </div>

          {/* Toggle Layout Button */}
          <button
            onClick={() => setIsGridLayout(!isGridLayout)}
            className="p-2 rounded-lg text-white bg-yellow-500 focus:outline-none"
          >
            {isGridLayout ? <Grid size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {/* Multi-Grid or List Layout for Products */}
      <div
        className={
          isGridLayout
            ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
            : "flex flex-col gap-4"
        }
      >
        {sortedBars.map((item) => (
          <div
            key={item.id}
            className={`${
              isDarkMode ? "bg-black" : "bg-white"
            } rounded-lg shadow-md p-3 relative group cursor-pointer ${
              isGridLayout ? "" : "flex items-center gap-[1.5rem]" // Keep grid view unchanged
            }`} // Keep grid view unchanged
            onClick={() => handleProductClick(item.product)}
          >
            {/* Image for both views */}
            <img
              src={item.product.photo[0].url}
              alt={item.product.name}
              className={`${
                isGridLayout
                  ? "w-full h-32 sm:h-48 object-contain mb-2 rounded"
                  : "h-[20vh] w-[25vh] object-contain rounded md:h-[50vh] md:w-[60vh]" // Mobile view and desktop view sizes
              } ${
                isGridLayout
                  ? "group-hover:opacity-50 transition-opacity duration-300"
                  : ""
              }`} // Adjust image size for list view
            />

            {/* Product name, price, and button for list view */}
            {!isGridLayout && (
              <div className="flex flex-col justify-between w-full gap-4">
                {/* No additional margin needed here */}
                <h2 className="text-sm sm:text-lg font-semibold mb-1">
                  {item.product.name}
                </h2>
                <p
                  className={`${
                    isDarkMode ? "text-yellow-500" : "text-yellow-500"
                  } text-sm mb-2 sm:mb-0`}
                >
                  <span dangerouslySetInnerHTML={{ __html: currency }} />
                  {item.product.price ? item.product.price : "0.00"}{" "}
                  {/* Updated to show 0.00 if price is not available */}
                </p>
                <div className="flex justify-start">
                  {/* Align button to the left */}
                  <button className="bg-yellow-500 text-white py-2 px-4 rounded-full flex items-center text-sm">
                    <FaShoppingCart className="mr-2" />
                    Add to Cart
                  </button>
                </div>
              </div>
            )}

            {/* Product name and price below the image for grid view */}
            {isGridLayout && (
              <div className="flex flex-col justify-between">
                <h2
                  className={`text-sm sm:text-lg font-semibold mb-1 ${
                    isDarkMode ? "text-white" : "text-black"
                  }`}
                >
                  {item.product.name}
                </h2>
                <p className="text-yellow-500 text-sm mb-2">
                  <span dangerouslySetInnerHTML={{ __html: currency }} />
                  {item.product.price ? item.product.price : "0.00"}{" "}
                  {/* Updated to show 0.00 if price is not available */}
                </p>
              </div>
            )}

            {/* Add to Cart button displayed on hover for grid view */}
            {isGridLayout && (
              <div className="absolute inset-0 flex justify-center items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black bg-opacity-50">
                <button
                  onClick={() => navigate("/cart")}
                  className="bg-yellow-500 text-white py-2 px-4 rounded-full flex items-center text-sm"
                >
                  <FaShoppingCart className="mr-2" />
                  Add to Cart
                  
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SwissbarDisplay;
